/*
 * memoryLog — the single place that turns in-app actions into agentic_engine
 * memory events, so EVERY mode writes a consistent, per-user signal keyed on the
 * SAME concept ids the Knowledge Hub exposes.
 *
 * concept ids: hierarchy levels join with dots; within-level slug uses
 * underscores (canonical, matches knowledge_hub.config.slug), e.g. "Two Pointers"
 * → "dsa.two_pointers". DSA tags slug through `fromText`; AI/ML modes pass the
 * session's selected hub node ids straight through.
 * All calls are fire-and-forget (never block or break the UX).
 */
import APIService from '../../services/APIService';

// canonical within-level slug (underscore) — matches the backend.
const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
export const conceptId = (surface, text) => `${surface}.${slug(text)}`;
const fromText = (surface, arr) => (arr || []).filter(Boolean).map((t) => conceptId(surface, t));

/** Canonical hub concept ids selected for a session (from its config). */
export const scopeIds = (config) => (config?.subjects || []).map((s) => s.id).filter(Boolean);

const log = (evt) => { try { APIService.recordEngineEvent(evt); } catch { /* never break UX */ } };

// ── DSA (tags slug to dsa.<tag>) ─────────────────────────────────────────────
export const logDsaAttempt = (qid, tags, correct, verdict) =>
  log({ surface: 'dsa', type: 'dsa.attempt', question_id: Number(qid),
    concept_ids: fromText('dsa', tags), outcome: { correct: !!correct }, payload: { verdict } });

export const logDsaSolved = (qid, tags) =>
  log({ surface: 'dsa', type: 'dsa.solved', question_id: Number(qid),
    concept_ids: fromText('dsa', tags), outcome: { correct: true } });

export const logDsaTool = (qid, tool, tags) =>
  log({ surface: 'dsa',
    type: (tool === 'give_hint' || tool === 'hint') ? 'dsa.hint_used' : 'dsa.tool_used',
    question_id: Number(qid), concept_ids: fromText('dsa', tags), payload: { tool } });

// ── AI/ML modes — concept_ids are canonical hub ids (from the session scope) ──
export const logMcqAnswered = (conceptIds, correct, payload) =>
  log({ surface: 'mcq', type: 'mcq.answered',
    concept_ids: conceptIds || [], outcome: { correct: !!correct }, payload });

export const logFlashcard = (conceptIds, rating, meta) =>
  log({ surface: 'flashcards', type: 'flashcard.feedback',
    concept_ids: conceptIds || [],
    outcome: { correct: rating === 'good' || rating === 'easy' },
    effort: { rating }, payload: { rating, ...(meta || {}) } });

export const logAsked = (surface, conceptIds, text) =>
  log({ surface, type: `${surface}.asked`,
    concept_ids: conceptIds || [], payload: { q: String(text || '').slice(0, 160) } });

export const logVoiceInterview = (report, conceptIds) =>
  log({ surface: 'voice', type: 'voice.interview',
    concept_ids: conceptIds || fromText('voice', report?.topics),
    outcome: { score: report?.overall_score ?? null, correct: (report?.overall_score ?? 0) >= 60 },
    payload: { questions: (report?.evaluations || report?.per_question || []).length } });

export const logRapidfireQuestion = (conceptIds, status) =>
  log({ surface: 'rapidfire', type: 'rapidfire.answered',
    concept_ids: conceptIds || [],
    outcome: { correct: status === 'correct' },
    payload: { status } });

export const logRapidfireRound = (report, conceptIds) =>
  log({ surface: 'rapidfire', type: 'rapidfire.round',
    concept_ids: conceptIds || fromText('rapidfire', report?.topics),
    outcome: { score: report?.overall_score ?? null, correct: (report?.overall_score ?? 0) >= 60 },
    payload: { counts: report?.metrics?.rapidfire_counts || {} } });

// Fallback for callers that only have free-text topics (no hub scope yet).
export const conceptsFromText = fromText;
